/**
 * Product stock check
 *
 * 商品ページの「カートに入れる」前に、入力数量が在庫を超えていないかをフロント側で検証する。
 * - 対象: 在庫追跡 = shopify かつ 在庫切れ時に販売停止 (policy = deny) の variant のみ
 * - 継続販売 (continue) / 未追跡は oversell 許容のため常に通す
 * - Shopify と同じく「カート内の同 variant 数量 + 入力数量」で判定 (/cart.js を初回 + cartUpdate で取得)
 * - 超過時は既存の .product-form__error-message-wrapper に「在庫が足りません(残りN点)」を表示し、
 *   submit を capture phase で先取りして product-form.js のカート追加処理ごと阻止する
 *
 * 選択中 variant の在庫ソース: (1) window.productVariants を選択中 variant id で照合
 * (実際に選択中の variant を正確に。id は全商品一意なので別商品データに汚染されても安全) →
 * (2) アクティブなサイズセレクタ (fallback) → (3) add-to-cart ボタンの data 属性 (fallback)。
 */
(function () {
  'use strict';

  // 在庫超過判定 (純ロジック / unit テスト対象)
  function exceedsStock(quantity, inventory, policy, management) {
    if (management !== 'shopify') return false;
    if (policy !== 'deny') return false;
    var inv = parseInt(inventory, 10);
    if (isNaN(inv)) return false;
    return quantity > inv;
  }

  // カート内の variant 別数量。Shopify は「カート内数量 + 追加数 ≤ 在庫」で判定するため、
  // 事前チェックでも既存カート数量を併算する。初回 + cartUpdate 時に /cart.js から更新。
  var cartQtyByVariant = {};

  function refreshCart() {
    if (typeof fetch !== 'function') return;
    var url = window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var map = {};
        (cart.items || []).forEach(function (item) {
          map[item.id] = (map[item.id] || 0) + item.quantity;
        });
        cartQtyByVariant = map;
      })
      .catch(function () {
        // 取得失敗時は 0 扱い (サーバ /cart/add.js が最終判定するので安全側)
      });
  }

  function getSelectedStock(scope, form) {
    // (1) 選択中 variant id を window.productVariants から引く (最優先)。
    // hidden [name="id"] は picker が選択時に同期更新するため、size+色 等の多オプション商品で
    // 初期ロード時に非先頭の組合せが選ばれていても、常に「実際に選択中の variant」の在庫を得る
    // (size-selector の dataset は色変更まで先頭 variant の値のままになりうるので先に id で引く)。
    // global だが、Shopify の variant id は全商品で一意なので、複数商品ページで別商品データに
    // 上書きされてもこの form の id とは一致せず、誤参照せず安全に (2)(3) へフォールバックする。
    var idInput = form.querySelector('[name="id"]');
    var variants = typeof window !== 'undefined' ? window.productVariants : null;
    if (idInput && variants && variants.length) {
      var id = parseInt(idInput.value, 10);
      var v = variants.find(function (x) {
        return parseInt(x.id, 10) === id;
      });
      // 在庫管理フィールドが揃っている場合のみ採用。欠けていれば (2)(3) のフォールバックに委ねる。
      if (v && v.inventory_management !== undefined) {
        return {
          inventory: v.inventory_quantity,
          policy: v.inventory_policy,
          management: v.inventory_management,
        };
      }
    }
    // (2) アクティブなサイズセレクタ (scope 内, フォールバック)
    var activeSize = scope.querySelector('.size-selector[data-active="true"]');
    if (activeSize) {
      return {
        inventory: activeSize.dataset.inventory,
        policy: activeSize.dataset.inventoryPolicy,
        management: activeSize.dataset.inventoryManagement,
      };
    }
    // (3) add-to-cart ボタンの data (最終フォールバック)
    var btn = scope.querySelector('#add-to-cart-button');
    if (btn) {
      return {
        inventory: btn.dataset.inventoryQuantity,
        policy: btn.dataset.inventoryPolicy,
        management: btn.dataset.inventoryManagement,
      };
    }
    return null;
  }

  function init(form) {
    var productForm = form.closest('product-form');
    var scope = form.closest('product-info') || productForm || form.closest('.product-custom-buy-buttons') || document;
    var sectionId = productForm ? productForm.getAttribute('data-section-id') : null;
    var qtyInput = form.querySelector('input[name="quantity"]');
    if (!qtyInput) return;
    // error wrapper は {% form %} の外 (product-form 直下、form の兄弟) に描画されるため、
    // form 内ではなく product-form / scope から探す。
    var wrapper = (productForm || scope).querySelector('.product-form__error-message-wrapper');
    var messageEl = wrapper ? wrapper.querySelector('.product-form__error-message') : null;
    var tpl = wrapper ? wrapper.getAttribute('data-insufficient-stock-tpl') : null;

    function showError(count) {
      if (!wrapper || !messageEl) return;
      // tpl が未設定、または en/ja 以外で翻訳が解決できず "translation missing" 等の
      // プレースホルダ無し文字列になった場合は、可読なフォールバックを使う
      // (生の translation-missing をユーザに見せない)。
      var hasTpl = tpl && tpl.indexOf('__COUNT__') !== -1;
      // フォールバックは store の主言語である日本語 (locale キー未解決時の最終手段。
      // 通常は en/ja の locale 値、または Shopify の default locale 値が使われる)。
      var text = hasTpl ? tpl.replace('__COUNT__', count) : '在庫が足りません(残り' + count + '点)';
      messageEl.textContent = text;
      wrapper.removeAttribute('hidden');
    }

    function clearError() {
      if (wrapper) wrapper.setAttribute('hidden', '');
    }

    function selectedVariantId() {
      var idInput = form.querySelector('[name="id"]');
      return idInput ? parseInt(idInput.value, 10) : null;
    }

    // 在庫超過なら true (= 追加不可)。カート内既存数量 + 入力数量 で判定する。
    function isInvalid() {
      var stock = getSelectedStock(scope, form);
      if (!stock) return false;
      var inputQty = parseInt(qtyInput.value, 10) || 0;
      var vid = selectedVariantId();
      var inCart = vid && cartQtyByVariant[vid] ? cartQtyByVariant[vid] : 0;
      if (exceedsStock(inputQty + inCart, stock.inventory, stock.policy, stock.management)) {
        showError(parseInt(stock.inventory, 10));
        return true;
      }
      return false;
    }

    qtyInput.addEventListener('change', function () {
      if (!isInvalid()) clearError();
    });

    // 祖先 (product-form。無ければ scope) の capture phase で先取りする。
    // form 自身に capture を付けても target phase では product-form.js の listener と
    // 登録順で並ぶだけで先取りできないため、祖先の capture phase で止める必要がある。
    // listener は自 form 由来の submit のみ処理する (event.target でガード)。
    (productForm || scope).addEventListener(
      'submit',
      function (event) {
        if (event.target !== form) return;
        if (isInvalid()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    // 変種 (サイズ/色/オプション) 変更時に再検証する。variant picker は選択時に
    // variant id を同期更新してから variant:gallery:update を dispatch するため、
    // 在庫が足りる variant に変えれば古いエラーが消え、別の超過 variant なら残り数が更新される。
    // 自セクションの変更のみ処理する (複数商品ページで無関係なフォームを走らせない)。
    document.addEventListener('variant:gallery:update', function (event) {
      if (sectionId && event.detail && String(event.detail.sectionId) !== String(sectionId)) return;
      if (!isInvalid()) clearError();
    });
  }

  function boot() {
    document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach(init);
    refreshCart();
    // カート変更 (追加/更新) 後に再取得して併算を最新化する
    if (typeof window.subscribe === 'function') {
      var evt = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
      window.subscribe(evt, refreshCart);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
